// React
import React, { useMemo } from 'react';

// components
import ADGMItemDetails from 'aurora-components/ADGMItemDetails/ADGMItemDetails';
import { ADGMGallery, ADGMImage, LightTooltipComponent } from 'aurora-components';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Paper from '@material-ui/core/Paper';

// utils
import format from 'date-fns/format';
import { keyEmptyCheck } from 'aurora-utils/utils';
import PropTypes from 'prop-types';

// Redux
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

// Styles
import withStyles from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import { useBoolean } from 'aurora-hooks';
import { isEmpty } from 'lodash';
import { Box } from '@material-ui/core';
import PropertyDetailHeader from './Header';
import styles from './styles';

const UNIT_LABELS_AND_VALUES = [
  {
    label: 'district',
    valueProperty: 'districtEn',
    valuePropertyAr: 'districtAr',
  },
  {
    label: 'community',
    valueProperty: 'communityEn',
    valuePropertyAr: 'communityAr',
  },
  {
    label: 'plot_number',
    valueProperty: 'plotNumber',
  },
  // {
  //   label: 'plot_address',
  //   valueProperty: 'plotAddress',
  // },
  {
    label: 'unit_classification',
    valueProperty: 'unitClassificationEn',
    valuePropertyAr: 'unitClassificationAr',
  },
  // {
  //   label: 'unt_reg_number',
  //   valueProperty: 'unitRegistrationNumber',
  // },
  {
    label: 'unit_area',
    customRender: values => (
      <ADGMItemDetails
        label='unit_area'
        value={values?.unitArea ? 'area_unit' : ''}
        valueLabel={{ s: values?.unitArea || '-' }}
        isLokalisedValue
      />
    ),
  },
  {
    label: 'unit_use',
    valueProperty: 'unitUsageTypeEn',
    valuePropertyAr: 'unitUsageTypeAr',
  },
  {
    label: 'unit_number',
    valueProperty: 'unitNumber',
  },

  // {
  //   label: 'building_registration_number_name',
  //   valueProperty: 'buildingRegNumber',
  // },
  {
    label: 'Land_use',
    valueProperty: 'landUseEn',
    valuePropertyAr: 'landUseAr',
  },
  // {
  //   label: 'premise_id',
  //   valueProperty: 'premiseNumber',
  // },
  // {
  //   label: 'shared_premise_id',
  //   valueProperty: 'sharedPremiseNumber',
  // },
  {
    label: 'building',
    valueProperty: 'buildingNumber',
  },
  {
    label: 'Floor',
    valueProperty: 'floorNumber',
  },

  {
    label: 'number_of_rooms',
    valueProperty: 'bedroomCount',
  },
  {
    label: 'project_name',
    customRender: values => {
      const lang = localStorage.getItem('lang') || 'en';
      const projectDetails = values?.projectDetails?.[0];
      return (
        <ADGMItemDetails
          label='project_name'
          value={lang === 'en' ? projectDetails?.projectNameEn : projectDetails?.projectNameAr}
        />
      );
    },
  },
  {
    label: 'project_number',
    customRender: values => (
      <ADGMItemDetails label='project_number' value={values?.projectDetails?.[0]?.projectNumber} />
    ),
  },

  {
    label: 'Ownership_type',
    customRender: values => (
      <ADGMItemDetails
        label='Ownership_type'
        value={values?.leaseHoldContracts?.length > 0 ? keyEmptyCheck('lease_hold') : keyEmptyCheck('free_hold')}
      />
    ),
  },
];

const PLOT_LABELS_AND_VALUES = [
  {
    label: 'district',
    valueProperty: 'districtEn',
    valuePropertyAr: 'districtAr',
  },
  {
    label: 'community',
    valueProperty: 'communityEn',
    valuePropertyAr: 'communityAr',
  },
  {
    label: 'plot_number',
    valueProperty: 'plotNumber',
    valuePropertyAr: 'plotNumber',
  },

  // {
  //   label: 'plot_address',
  //   valueProperty: 'plotAddress',
  // },
  {
    label: 'Allocation_type',
    valueProperty: 'allocationTypeEn',
    valuePropertyAr: 'allocationTypeAr',
  },
  {
    label: 'land_area',
    customRender: values => (
      <ADGMItemDetails label='land_area' value='area_unit' valueLabel={{ s: values?.landSize }} isLokalisedValue />
    ),
  },
  {
    label: 'Land_use',
    valueProperty: 'landUseEn',
    valuePropertyAr: 'landUseAr',
  },
  {
    label: 'construction_status',
    customRender: (values, isAr) => (
      <ADGMItemDetails
        label='construction_status'
        value={isAr ? values?.constructionStatusAr : values?.constructionStatusEn}
      />
    ),
  },
  {
    label: 'construction_date',
    customRender: values => (
      <ADGMItemDetails
        label='construction_date'
        value={values?.constructionDate ? format(new Date(values?.constructionDate), 'dd/MM/yyyy') : '~'}
      />
    ),
  },
  {
    label: 'project_number',
    customRender: values => (
      <ADGMItemDetails label='project_number' value={values?.projectDetails?.[0]?.projectNumber} />
    ),
  },
  {
    label: 'project_status',
    customRender: values => (
      <ADGMItemDetails label='project_status' value={keyEmptyCheck(values?.projectDetails?.[0]?.registrationType)} />
    ),
  },
  {
    label: 'Ownership_type',
    customRender: values => (
      <ADGMItemDetails
        label='Ownership_type'
        value={values?.leaseHoldContracts?.length > 0 ? keyEmptyCheck('lease_hold') : keyEmptyCheck('free_hold')}
      />
    ),
  },
  // {
  //   label: 'service_status',
  //   customRender: (values, isAr) => (
  //     <ADGMItemDetails label='service_status' value={isAr ? values?.serviceStatusAr : values?.serviceStatusEn} />
  //   ),
  // },
  // {
  //   label: 'axis_status',
  //   customRender: values => (
  //     <ADGMItemDetails
  //       label='axis_status'
  //       value={
  //         values?.onCommercialAxis
  //           ? keyEmptyCheck('COMMERCIAL')
  //           : values?.onInvestmentAxis
  //           ? keyEmptyCheck('INVESTMENT')
  //           : '~'
  //       }
  //     />
  //   ),
  // },
];

/**
  A reuseable component that can be used anywhere in the application 
  which takes the following params as props
 * @param { object } property - Object for property details
 * @param { string } applicationType - will be used to set the active tab index  
 * @param { object } application - application details  
 * @param { bool } topBarHidden - to activate the top bar header  
 * @returns { node } - return a Panel having Property Details
*/

const PropertyDetailsV2 = ({ classes, property, topBarHidden }) => {
  if (isEmpty(property)) return null;

  const { lang } = useSelector(
    createSelector(
      state => state.language,
      language => ({
        lang: language.locale,
      }),
    ),
  );

  const mapDialog = useBoolean();

  const isAr = lang === 'ar';
  const UNIT = property?.propertyType === 4;
  const PLOT = property?.propertyType === 1;

  const labelsAndValues = useMemo(() => {
    return UNIT ? UNIT_LABELS_AND_VALUES : PLOT_LABELS_AND_VALUES;
  }, [property]);
  return (
    <>
      {!topBarHidden && <PropertyDetailHeader property={property} lang={lang} />}
      <Paper className={classNames(classes.marginBottom, classes.propertyDetailsContainer)}>
        <div className={classes.titleHead}>
          <div className={classes.title}>
            <span>{keyEmptyCheck('property_details')}</span>
          </div>
        </div>
        <div className={classes.detailsWrapper}>
          <Grid container>
            <Grid item xs={12} md={9}>
              <Grid container>
                {labelsAndValues.map(item => {
                  return (
                    <Grid item key={item.label} xs={12} md={4} className={classes.itemGrid}>
                      {item.customRender ? (
                        item.customRender(property, isAr)
                      ) : (
                        <ADGMItemDetails
                          label={item.label}
                          value={
                            isAr && item.valuePropertyAr
                              ? property?.[item.valuePropertyAr]
                              : property?.[item.valueProperty]
                          }
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
            <Hidden smDown>
              <Grid item md={3} style={{ textAlign: 'right' }}>
                <div className={classes.cardimageFrame}>
                  {property?.propertyMap ? (
                    <ADGMImage
                      onClick={mapDialog.onTrue}
                      httpURL={`data:image/png;base64,${property?.propertyMap}`}
                      alt='property Image2'
                    />
                  ) : (
                    <LightTooltipComponent title={keyEmptyCheck(' no_map_available')}>
                      <Box>
                        <ADGMImage
                          name={PLOT ? 'land_property.svg' : 'PropertyDetails-Unit.svg'}
                          alt='property Image2'
                        />
                      </Box>
                    </LightTooltipComponent>
                  )}
                </div>
              </Grid>
            </Hidden>
          </Grid>
        </div>
      </Paper>
      <ADGMGallery
        open={mapDialog.value}
        title={keyEmptyCheck('map_view')}
        images={[`data:image/png;base64,${property?.propertyMap}`]}
        toggleModal={mapDialog.onFalse}
        handleCloseModal={mapDialog.toggle}
      />
    </>
  );
};
PropertyDetailsV2.propTypes = {
  classes: PropTypes.object.isRequired,
  topBarHidden: PropTypes.bool.isRequired,
  property: PropTypes.object.isRequired,
};

export default withStyles(styles)(PropertyDetailsV2);
